import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { CanvasContext } from "../../contexts/CanvasContext";
import "./LandingNavbar.css";

const LandingNavbar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isDarkTheme, setIsDarkTheme } = useContext(CanvasContext);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      setVisible(prevScrollPos > currentScrollPos || currentScrollPos < 10);
      setPrevScrollPos(currentScrollPos);
      setIsScrolled(currentScrollPos > 50); // Add threshold for logo change
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos]);

  useEffect(() => {
    const root = document.getElementById("root");
    root.classList.toggle("dark", isDarkTheme);
  }, [isDarkTheme]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen((prevState) => !prevState);
  };

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  const handleNavigation = (to) => {
    setMenuOpen(false);
    navigate(to);
    window.scrollTo(0, 0);
  };

  const handleScrollTo = (id) => {
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`navbar ${isDarkTheme ? "dark-theme" : ""} ${
        visible ? "" : "navbar--hidden"
      }`}
    >
      <div className={`navbar__container ${isDarkTheme ? "dark-theme" : ""}`}>
        <div className="navbar__logo">
          <Link
            to="/"
            className={`navbar__link ${isDarkTheme ? "dark-theme" : ""}`}
            onClick={() => handleNavigation("/")}
          >
            <div className="animated-logo">
              <span className={`logo-text ${isScrolled ? "scrolled" : ""}`}>
                {isScrolled ? "NG" : "NexGen"}
              </span>
            </div>
          </Link>
        </div>

        <div className="menu cross menu--1" ref={menuButtonRef}>
          <label>
            <input type="checkbox" checked={menuOpen} onChange={toggleMenu} />
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <path className="line--1" d="M0 40h62c13 0 6 28-4 18L35 35" />
              <path className="line--2" d="M0 50h70" />
              <path className="line--3" d="M0 60h62c13 0 6-28-4-18L35 65" />
            </svg>
          </label>
        </div>

        <ul
          ref={menuRef}
          className={`navbar__menu ${isDarkTheme ? "dark-theme" : ""} ${
            menuOpen ? "navbar__menu--open" : ""
          }`}
        >
          <li className="navbar__item">
            <Link
              to="/documentation"
              className={`navbar__link ${isDarkTheme ? "dark-theme" : ""} ${
                location.pathname === "/documentation" ? "active" : ""
              }`}
              onClick={() => handleNavigation("/documentation")}
            >
              Documentation
            </Link>
          </li>
          <li className="navbar__item">
            <Link
              to="/about"
              className={`navbar__link ${isDarkTheme ? "dark-theme" : ""} ${
                location.pathname === "/about" ? "active" : ""
              }`}
              onClick={() => handleNavigation("/about")}
            >
              About
            </Link>
          </li>
          <li className="navbar__item">
            <Link
              to="/#services"
              className={`navbar__link ${isDarkTheme ? "dark-theme" : ""} ${
                location.hash === "#services" ? "active" : ""
              }`}
              onClick={() => handleScrollTo("services")}
            >
              Services
            </Link>
          </li>
          <li className="navbar__item">
            <Link
              to="/#contact"
              className={`navbar__link ${isDarkTheme ? "dark-theme" : ""} ${
                location.hash === "#contact" ? "active" : ""
              }`}
              onClick={() => handleScrollTo("contact")}
            >
              Contact Us
            </Link>
          </li>
          {!user ? (
            <>
              <li className="navbar__item">
                <NavLink
                  className={`navbar__link ${isDarkTheme ? "dark-theme" : ""} ${
                    location.pathname === "/signup" ? "active" : ""
                  }`}
                  to="/signup"
                  onClick={() => handleNavigation("/signup")}
                >
                  Sign Up
                </NavLink>
              </li>
              <li className="navbar__item">
                <NavLink
                  className={`navbar__link ${isDarkTheme ? "dark-theme" : ""} ${
                    location.pathname === "/signin" ? "active" : ""
                  }`}
                  to="/signin"
                  onClick={() => handleNavigation("/signin")}
                >
                  Sign In
                </NavLink>
              </li>
            </>
          ) : (
            <>
              <li className="navbar__item">
                <NavLink
                  className={`navbar__link ${isDarkTheme ? "dark-theme" : ""}`}
                  to="/editor"
                  onClick={() => handleNavigation("/editor")}
                >
                  Editor
                </NavLink>
              </li>
              <li className="navbar__item">
                <NavLink
                  className={`navbar__link ${isDarkTheme ? "dark-theme" : ""}`}
                  to="/profile"
                  onClick={() => handleNavigation("/profile")}
                >
                  Profile
                </NavLink>
              </li>
            </>
          )}
          <li className="navbar__item">
            <label className="switch__wrapper">
              <input
                type="checkbox"
                id="theme__switch"
                checked={isDarkTheme}
                onChange={toggleTheme}
              />
              <div className="switch__slider">
                <span className="cloud cloud1" />
                <span className="cloud cloud2" />
                <span className="cloud cloud3" />
                <span className="star star1"></span>
                <span className="star star2"></span>
                <span className="star star3"></span>
                <span className="star star4"></span>
              </div>
            </label>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default LandingNavbar;


// import React, { useState, useEffect, useContext, useRef } from "react";
// import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
// import { useAuth } from "../../contexts/AuthContext";
// import { CanvasContext } from "../../contexts/CanvasContext";
// import "./LandingNavbar.css";

// const LandingNavbar = () => {
//   const location = useLocation();
//   const { user } = useAuth();
//   const menuRef = useRef(null);
//   const menuButtonRef = useRef(null);
//   const navigate = useNavigate();
//   const [menuOpen, setMenuOpen] = useState(false);
//   const [prevScrollPos, setPrevScrollPos] = useState(0);
//   const [visible, setVisible] = useState(true);
//   const { isDarkTheme, setIsDarkTheme } = useContext(CanvasContext);

//   useEffect(() => {
//     const handleScroll = () => {
//       const currentScrollPos = window.pageYOffset;
//       setVisible(prevScrollPos > currentScrollPos || currentScrollPos < 10);
//       setPrevScrollPos(currentScrollPos);
//     };

//     window.addEventListener("scroll", handleScroll);
//     return () => window.removeEventListener("scroll", handleScroll);
//   }, [prevScrollPos]);

//   useEffect(() => {
//     const root = document.getElementById("root");
//     root.classList.toggle("dark", isDarkTheme);
//   }, [isDarkTheme]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         menuRef.current &&
//         !menuRef.current.contains(event.target) &&
//         !menuButtonRef.current.contains(event.target)
//       ) {
//         setMenuOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   const toggleMenu = () => {
//     setMenuOpen((prevState) => !prevState);
//   };

//   const toggleTheme = () => {
//     setIsDarkTheme(!isDarkTheme);
//   };

//   const handleNavigation = (to) => {
//     setMenuOpen(false);
//     navigate(to);
//     window.scrollTo(0, 0);
//   };

//   const handleScrollTo = (id) => {
//     setMenuOpen(false);
//     const element = document.getElementById(id);
//     if (element) {
//       element.scrollIntoView({ behavior: "smooth" });
//     }
//   };
//   return (
//     <nav
//       className={`navbar 
//         ${isDarkTheme ? "dark-theme" : ""} 
//         ${visible ? "" : "navbar--hidden"}`}
//     >
//       <div className={`navbar__container ${isDarkTheme ? "dark-theme" : ""}`}>
//         <div className="navbar__logo">
//           <Link
//             to="/"
//             className={`navbar__link ${isDarkTheme ? "dark-theme" : ""}`}
//             onClick={() => handleNavigation("/")}
//           >
//             <div className="animated-logo">
//               <span className="logo-text">nexGen</span>
//             </div>
//           </Link>
//         </div>
//         <div className="menu cross menu--1" ref={menuButtonRef}>
//           <label>
//             <input type="checkbox" checked={menuOpen} onChange={toggleMenu} />
//             <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
//               <path className="line--1" d="M0 40h62c13 0 6 28-4 18L35 35" />
//               <path className="line--2" d="M0 50h70" />
//               <path className="line--3" d="M0 60h62c13 0 6-28-4-18L35 65" />
//             </svg>
//           </label>
//         </div>
//         <ul
//           ref={menuRef}
//           className={`navbar__menu ${isDarkTheme ? "dark-theme" : ""} ${
//             menuOpen ? "navbar__menu--open" : ""
//           }`}
//         >
//           <li className="navbar__item">
//             <Link
//               to="/documentation"
//               className={`navbar__link 
//                 ${isDarkTheme ? "dark-theme" : ""}
//                 ${location.pathname === "/documentation" ? "active" : ""}
//                 `}
//               onClick={() => handleNavigation("/documentation")}
//             >
//               Documentation
//             </Link>
//           </li>
//           <li className="navbar__item">
//             <Link
//               to="/about"
//               className={`navbar__link 
//                 ${isDarkTheme ? "dark-theme" : ""}
//                 ${location.pathname === "/about" ? "active" : ""}
//                 `}
//               onClick={() => handleNavigation("/about")}
//             >
//               About
//             </Link>
//           </li>
//           <li className="navbar__item">
//             <Link
//               to="/#services"
//               className={`navbar__link 
//                 ${isDarkTheme ? "dark-theme" : ""}
//                 ${location.hash === "#services" ? "active" : ""}
//                 `}
//               onClick={() => handleScrollTo("services")}
//             >
//               Services
//             </Link>
//           </li>
//           <li className="navbar__item">
//             <Link
//               to="/#contact"
//               className={`navbar__link 
//                 ${isDarkTheme ? "dark-theme" : ""} 
//                 ${location.hash === "#contact" ? "active" : ""}
//                 `}
//               onClick={() => handleScrollTo("contact")}
//             >
//               Contact Us
//             </Link>
//           </li>
//           {!user ? (
//             <>
//               <li className="navbar__item">
//                 <NavLink
//                   className={`navbar__link ${isDarkTheme ? "dark-theme" : ""}
//                      ${location.pathname === "/signup" ? "active" : ""}
//                      `}
//                   to="/signup"
//                   onClick={() => handleNavigation("/signup")}
//                 >
//                   Sign Up
//                 </NavLink>
//               </li>
//               <li className="navbar__item">
//                 <NavLink
//                   className={`navbar__link 
//                     ${isDarkTheme ? "dark-theme" : ""}
//                      ${location.pathname === "/signin" ? "active" : ""}

//                     `}
//                   to="/signin"
//                   onClick={() => handleNavigation("/signin")}
//                 >
//                   Sign In
//                 </NavLink>
//               </li>
//             </>
//           ) : (
//             <>
//               <li className="navbar__item">
//                 <NavLink
//                   className={`navbar__link ${isDarkTheme ? "dark-theme" : ""}`}
//                   to="/editor"
//                   onClick={() => handleNavigation("/editor")}
//                 >
//                   Editor
//                 </NavLink>
//               </li>
//               <li className="navbar__item">
//                 <NavLink
//                   className={`navbar__link ${isDarkTheme ? "dark-theme" : ""}`}
//                   to="/profile"
//                   onClick={() => handleNavigation("/profile")}
//                 >
//                   Profile
//                 </NavLink>
//               </li>
//             </>
//           )}
//           <li className="navbar__item">
//             <label className="switch__wrapper">
//               <input
//                 type="checkbox"
//                 id="theme__switch"
//                 checked={isDarkTheme}
//                 onChange={toggleTheme}
//               />
//               <div className="switch__slider">
//                 <span className="cloud cloud1" />
//                 <span className="cloud cloud2" />
//                 <span className="cloud cloud3" />
//                 <span className="star star1"></span>
//                 <span className="star star2"></span>
//                 <span className="star star3"></span>
//                 <span className="star star4"></span>
//               </div>
//             </label>
//           </li>
//         </ul>
//       </div>
//     </nav>
//   );
// };

// export default LandingNavbar;
